import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { getInitials, getRandomColor, formatPhoneNumber, shouldShowApiToast } from '../../utils/helpers';
import { 
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import {
  getMessages,
  getMessage,
  createMessage,
  updateMessage,
  deleteMessage,
  bulkDeleteMessages,
  markAsRead,
  markAsUnread,
  flagMessage,
  unflagMessage,
  escalateMessage,
  replyMessage,
  getInboxStats,
  exportInbox,
  clearApiMessage,
  clearError,
  clearInboxState,
  clearCurrentMessage,
  clearMemberMessages,
  selectMessages,
  selectCurrentMessage,
  selectInboxStats,
  selectInboxPagination,
  selectInboxLoading,
  selectInboxError,
  selectInboxApiMessage,
  selectInboxSuccess,
  selectInboxExportData,
  selectInboxStatsSummary,
  selectUnreadCount,
} from '../../store/slices/engagement/inboxSlice';
import {
  Trash2,
  AlertTriangle,
  Download,
  BarChart3,
  FileJson,
  TrendingUp,
  FileText,
  Search,
  Send,
  Mail,
  Inbox as InboxIcon,
  Flag,
  Calendar,
  CheckCircle2,
  Siren,
  MessageSquare,
  User,
  Loader2,
} from 'lucide-react';

// ==================== HELPER: Format DateTime ====================
const formatDateTime = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit', 
        minute: '2-digit'
      });
    }
    return dateString;
  } catch (e) {
    return dateString;
  }
};

const Inbox = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const messages = useSelector(selectMessages) || [];
  const currentMessage = useSelector(selectCurrentMessage);
  const stats = useSelector(selectInboxStatsSummary);
  const pagination = useSelector(selectInboxPagination);
  const loading = useSelector(selectInboxLoading);
  const apiMessage = useSelector(selectInboxApiMessage);
  const error = useSelector(selectInboxError);
  const successMessage = useSelector(selectInboxSuccess);
  const unreadCount = useSelector(selectUnreadCount);
  const exportData = useSelector(selectInboxExportData);

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          dispatch(getMessages({ 
            per_page: itemsPerPage, 
            page: currentPage,
            search: searchTerm,
            status: filterType !== 'all' ? filterType : undefined
          })),
          dispatch(getInboxStats())
        ]);
      } catch (err) {
        console.error('Error loading inbox:', err);
      }
    };
    
    loadData();
    
    return () => {
      dispatch(clearInboxState());
    };
  }, [dispatch]);

  // Handle filters and pagination
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm
      };
      if (filterType !== 'all') {
        params.status = filterType;
      }
      
      dispatch(getMessages(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, filterType, currentPage, itemsPerPage, dispatch]);

  // Display API messages - with deduplication (matching Organization pattern)
  useEffect(() => {
    const message = apiMessage || successMessage;
    if (!message || !showToast || !shouldShowApiToast(message, !!error)) return;

    const messageKey = `${message}_${error ? 'error' : 'success'}`;
    const now = Date.now();

    if (lastToastRef.current.message === messageKey &&
      (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }

    lastToastRef.current = { message: messageKey, timestamp: now };

    const toastType = error ? 'error' : 'success';
    showToast(message, toastType);

    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, successMessage, error, dispatch, showToast]);

  // Load selected message when selectedMessageId changes
  useEffect(() => {
    if (selectedMessageId) {
      dispatch(getMessage(selectedMessageId));
    }
  }, [selectedMessageId, dispatch]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== PAGINATION ====================
  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.last_page || 1;
  const currentPageNum = pagination?.current_page || 1;
  const perPage = pagination?.per_page || 15;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) {
      setCurrentPage(currentPageNum - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) {
      setCurrentPage(currentPageNum + 1);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== MESSAGE HANDLERS ====================

  const selectMessage = (msg) => {
    setSelectedMessageId(msg.id);
    if (msg.is_read === false) {
      dispatch(markAsRead(msg.id));
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      showToastMessage('Please type a reply', 'error');
      return;
    }
    
    if (!currentMessage) {
      showToastMessage('No message selected', 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      await dispatch(replyMessage({
        id: currentMessage.id,
        reply_message: replyText.trim()
      })).unwrap();
      showToastMessage('📨 Reply sent!', 'success');
      setReplyText('');
      await Promise.all([
        dispatch(getMessages({ 
          per_page: itemsPerPage, 
          page: currentPage,
          search: searchTerm,
          status: filterType !== 'all' ? filterType : undefined
        })),
        dispatch(getInboxStats())
      ]);
    } catch (err) {
      showToastMessage(err.message || 'Failed to send reply', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEscalate = async () => {
    if (!currentMessage) {
      showToastMessage('No message selected', 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      await dispatch(escalateMessage(currentMessage.id)).unwrap();
      showToastMessage('🚨 Escalated to Pastor', 'info');
      await Promise.all([
        dispatch(getMessages({ 
          per_page: itemsPerPage, 
          page: currentPage,
          search: searchTerm,
          status: filterType !== 'all' ? filterType : undefined
        })),
        dispatch(getInboxStats())
      ]);
    } catch (err) {
      showToastMessage(err.message || 'Failed to escalate message', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFlag = async () => {
    if (!currentMessage) {
      showToastMessage('No message selected', 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      await dispatch(flagMessage(currentMessage.id)).unwrap();
      showToastMessage('🚩 Flagged for pastoral follow-up', 'info');
      await Promise.all([
        dispatch(getMessages({ 
          per_page: itemsPerPage, 
          page: currentPage,
          search: searchTerm,
          status: filterType !== 'all' ? filterType : undefined
        })),
        dispatch(getInboxStats())
      ]);
    } catch (err) {
      showToastMessage(err.message || 'Failed to flag message', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMessage = (msg) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Message</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete this message from <strong>{msg.sender_name || msg.name}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This action cannot be undone.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              try {
                setIsSubmitting(true);
                await dispatch(deleteMessage(msg.id)).unwrap();
                closeModal();
                if (selectedMessageId === msg.id) {
                  setSelectedMessageId(null);
                }
                await Promise.all([
                  dispatch(getMessages({ 
                    per_page: itemsPerPage, 
                    page: currentPage,
                    search: searchTerm,
                    status: filterType !== 'all' ? filterType : undefined
                  })),
                  dispatch(getInboxStats())
                ]);
                showToastMessage(`🗑️ Message from ${msg.sender_name || msg.name} deleted`, 'error');
              } catch (err) {
                showToastMessage(err.message || 'Failed to delete message', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Deleting...</>
            ) : (
              'Delete Message'
            )}
          </button>
        </div>
      </div>
    );
  };

  const handleMarkAsRead = async (msg) => {
    try {
      await dispatch(markAsRead(msg.id)).unwrap();
      await Promise.all([
        dispatch(getMessages({ 
          per_page: itemsPerPage, 
          page: currentPage,
          search: searchTerm,
          status: filterType !== 'all' ? filterType : undefined
        })),
        dispatch(getInboxStats())
      ]);
      showToastMessage(`✅ Message from ${msg.sender_name || msg.name} marked as read`, 'success');
    } catch (err) {
      showToastMessage(err.message || 'Failed to mark message as read', 'error');
    }
  };

  const handleMarkAsUnread = async (msg) => {
    try {
      await dispatch(markAsUnread(msg.id)).unwrap();
      await Promise.all([
        dispatch(getMessages({ 
          per_page: itemsPerPage, 
          page: currentPage,
          search: searchTerm,
          status: filterType !== 'all' ? filterType : undefined
        })),
        dispatch(getInboxStats())
      ]);
      showToastMessage(`📩 Message from ${msg.sender_name || msg.name} marked as unread`, 'info');
    } catch (err) {
      showToastMessage(err.message || 'Failed to mark message as unread', 'error');
    }
  };

  // ==================== EXPORT FUNCTIONS ====================

  const handleExport = async (type, format = 'csv') => {
    try {
      setIsExporting(true);
      const result = await dispatch(exportInbox({ type, format })).unwrap();
      
      if (result.success && result.exportData) {
        const data = result.exportData;
        const filename = getExportFilename(`inbox-${type}`);
        
        switch (format.toLowerCase()) {
          case 'csv':
            exportToCSV(data, filename);
            break;
          case 'json':
            exportToJSON(data, filename);
            break;
          case 'excel':
            exportToExcel(data, filename);
            break;
          case 'pdf':
            exportToPDF(data, 'Inbox Report', filename);
            break;
          default:
            showToastMessage('Unsupported export format', 'error');
            setIsExporting(false);
            return;
        }
        
        showToastMessage(`📥 ${data.length} messages exported as ${format.toUpperCase()}!`, 'success');
        closeModal();
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to export messages', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // Open Export Modal
  const openExportModal = (exportType = 'all') => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px' }} /> Export Inbox Messages</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export messages in your preferred format.
            {exportType === 'unread' && (
              <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                · Unread messages
              </span>
            )}
            {exportType === 'all' && (
              <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                · All messages
              </span>
            )}
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleExport(exportType, format)}
                  disabled={isExporting}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: isExporting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isExporting ? (
                    <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <span style={{ fontSize: '20px' }}>
                      {format === 'CSV' && <BarChart3 size={20} />}
                      {format === 'JSON' && <FileJson size={20} />}
                      {format === 'Excel' && <TrendingUp size={20} />}
                      {format === 'PDF' && <FileText size={20} />}
                    </span>
                  )}
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{
            padding: '10px 12px',
            background: 'var(--cream)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={14} style={{ verticalAlign: '-3px' }} /> Messages will be exported</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // Export handlers
  const handleExportAll = () => {
    if (messages.length === 0) {
      showToastMessage('No messages to export', 'error');
      return;
    }
    openExportModal('all');
  };

  const handleExportUnread = () => {
    if (unreadCount === 0) {
      showToastMessage('No unread messages to export', 'error');
      return;
    }
    openExportModal('unread');
  };

  // ==================== RENDER ====================

  const statsData = stats || {
    totalMessages: 0,
    unreadMessages: 0,
    readMessages: 0,
    flaggedMessages: 0,
    escalatedMessages: 0,
    messagesByDay: {},
    topSenders: []
  };

  // Get current items for display
  const currentItems = messages;

  return (
    <div className="inbox-container" style={{ height: 'calc(100vh - 140px)' }}>
      <div className="grid-35-65" style={{ height: '100%' }}>
        {/* Inbox List */}
        <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, flexWrap: 'wrap' }}>
            <div className="search-box" style={{ maxWidth: 'none', flex: 1, minWidth: '120px' }}>
              <span className="search-icon"><Search size={14} /></span>
              <input 
                type="text" 
                placeholder="Search inbox…" 
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                title="Search messages by name, content, or phone"
                disabled={loading}
              />
            </div>
            <select 
              className="form-select" 
              style={{ width: 'auto', padding: '7px 10px', fontSize: '12px' }}
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setCurrentPage(1);
              }}
              title="Filter messages"
              disabled={loading}
            >
              <option value="all">All ({totalItems})</option>
              <option value="unread">Unread ({unreadCount})</option>
              <option value="read">Read ({totalItems - unreadCount})</option>
              <option value="flagged">Flagged ({statsData.flaggedMessages})</option>
              <option value="escalated">Escalated ({statsData.escalatedMessages})</option>
            </select>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-ghost btn-sm" 
                onClick={handleExportAll}
                title="Export all messages"
                style={{ fontSize: '11px', padding: '4px 8px' }}
                disabled={isSubmitting || loading || messages.length === 0}
              >
                <Download size={12} style={{ verticalAlign: '-2px' }} /> All
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleExportUnread}
                title="Export unread messages"
                style={{ fontSize: '11px', padding: '4px 8px' }}
                disabled={unreadCount === 0 || isSubmitting || loading}
              >
                <Send size={12} style={{ verticalAlign: '-2px' }} /> Unread
              </button>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', whiteSpace: 'nowrap' }}>
              {unreadCount} unread
            </div>
          </div>
          
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading && currentItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
                <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading messages...</div>
              </div>
            ) : currentItems.length > 0 ? (
              currentItems.map(msg => (
                <div
                  key={msg.id}
                  className={`inbox-item ${!msg.is_read ? 'unread' : ''}`}
                  onClick={() => selectMessage(msg)}
                  style={{ cursor: 'pointer', position: 'relative' }}
                >
                  {!msg.is_read ? <div className="inbox-dot"></div> : <div style={{ width: '8px' }}></div>}
                  <div style={{ 
                    width: '36px', 
                    height: '36px', 
                    borderRadius: '50%', 
                    background: msg.sender_color || '#0891b2', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '13px', 
                    fontWeight: '700', 
                    color: 'white', 
                    flexShrink: 0 
                  }}>
                    {msg.sender_initials || '?'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="inbox-name">{msg.sender_name || 'Unknown'}</div>
                    <div className="inbox-preview">{msg.message}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                    <div className="inbox-time">{formatDateTime(msg.created_at)}</div>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      <button 
                        className="btn btn-ghost btn-sm btn-icon" 
                        onClick={(e) => { e.stopPropagation(); openExportModal('selected'); }}
                        title="Export message"
                        style={{ fontSize: '11px', padding: '2px 4px' }}
                        disabled={loading || isSubmitting}
                      >
                        <Download size={13} />
                      </button>
                      <button
                        className="btn btn-ghost btn-sm btn-icon"
                        onClick={(e) => { e.stopPropagation(); handleDeleteMessage(msg); }}
                        title="Delete message"
                        style={{ fontSize: '11px', padding: '2px 4px' }}
                        disabled={loading || isSubmitting}
                      >
                        <Trash2 size={13} />
                      </button>
                      {!msg.is_read ? (
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={(e) => { e.stopPropagation(); handleMarkAsRead(msg); }}
                          title="Mark as read"
                          style={{ fontSize: '11px', padding: '2px 4px' }}
                          disabled={loading || isSubmitting}
                        >
                          <CheckCircle2 size={13} />
                        </button>
                      ) : (
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={(e) => { e.stopPropagation(); handleMarkAsUnread(msg); }}
                          title="Mark as unread"
                          style={{ fontSize: '11px', padding: '2px 4px' }}
                          disabled={loading || isSubmitting}
                        >
                          <Mail size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state" style={{ padding: '40px 20px' }}>
                <div className="empty-icon"><InboxIcon size={40} strokeWidth={1.5} /></div>
                <div className="empty-title">No messages found</div>
                <div className="empty-text">Try adjusting your search or filters</div>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '8px',
              padding: '10px 14px',
              borderTop: '1px solid var(--border)',
              flexShrink: 0
            }}>
              <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                {((currentPageNum - 1) * perPage) + 1}-{Math.min(currentPageNum * perPage, totalItems)} of {totalItems}
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={goToPreviousPage}
                  disabled={currentPageNum === 1 || loading}
                  style={{ 
                    opacity: currentPageNum === 1 ? 0.5 : 1, 
                    cursor: currentPageNum === 1 ? 'not-allowed' : 'pointer' 
                  }}
                  title="Previous page"
                >
                  ←
                </button>
                {getPageNumbers().map(page => (
                  <button 
                    key={page}
                    className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => goToPage(page)}
                    title={`Go to page ${page}`}
                    style={{ padding: '4px 8px', fontSize: '11px' }}
                    disabled={loading}
                  >
                    {page}
                  </button>
                ))}
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={goToNextPage}
                  disabled={currentPageNum === totalPages || loading}
                  style={{ 
                    opacity: currentPageNum === totalPages ? 0.5 : 1, 
                    cursor: currentPageNum === totalPages ? 'not-allowed' : 'pointer' 
                  }}
                  title="Next page"
                >
                  →
                </button>
              </div>
              <select 
                className="form-select" 
                style={{ width: 'auto', padding: '2px 6px', fontSize: '11px' }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                title="Items per page"
                disabled={loading}
              >
                <option value={3}>3</option>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
          )}
        </div>

        {/* Message Detail */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
          {currentMessage ? (
            <>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '50%', 
                  background: currentMessage.sender_color || '#e11d48', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '15px', 
                  fontWeight: '700', 
                  color: 'white',
                  flexShrink: 0
                }}>
                  {currentMessage.sender_initials || '?'}
                </div>
                <div style={{ flex: 1, minWidth: '100px' }}>
                  <div style={{ fontWeight: '600', color: 'var(--navy)' }}>{currentMessage.sender_name || 'Unknown'}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{currentMessage.sender_phone}</div>
                </div>
                <button 
                  className="btn btn-ghost btn-sm" 
                  onClick={() => openExportModal('selected')}
                  title="Export this message"
                  disabled={isSubmitting || loading}
                >
                  <Download size={14} style={{ verticalAlign: '-2px' }} /> Export
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={handleFlag}
                  title="Flag for follow-up"
                  disabled={isSubmitting || loading}
                >
                  <Flag size={14} style={{ verticalAlign: '-2px' }} /> Flag
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    if (currentMessage.is_read) {
                      handleMarkAsUnread(currentMessage);
                    } else {
                      handleMarkAsRead(currentMessage);
                    }
                  }}
                  title={currentMessage.is_read ? 'Mark as unread' : 'Mark as read'}
                  disabled={isSubmitting || loading}
                >
                  {currentMessage.is_read ? <><Mail size={14} style={{ verticalAlign: '-2px' }} /> Unread</> : <><CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> Read</>}
                </button>
                <button
                  className="btn btn-ghost btn-sm btn-icon"
                  onClick={() => handleDeleteMessage(currentMessage)}
                  title="Delete message"
                  style={{ color: 'var(--red)' }}
                  disabled={isSubmitting || loading}
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {/* Message Status Indicators */}
              <div style={{
                padding: '8px 20px',
                background: 'var(--cream)',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                alignItems: 'center',
                fontSize: '12px'
              }}>
                <span><Calendar size={13} style={{ verticalAlign: '-2px' }} /> {formatDateTime(currentMessage.created_at)}</span>
                {currentMessage.is_read && <span><CheckCircle2 size={13} style={{ verticalAlign: '-2px' }} /> Read</span>}
                {currentMessage.is_flagged && <span><Flag size={13} style={{ verticalAlign: '-2px' }} /> Flagged</span>}
                {currentMessage.is_escalated && <span><Siren size={13} style={{ verticalAlign: '-2px' }} /> Escalated</span>}
                {currentMessage.replied_at && <span><MessageSquare size={13} style={{ verticalAlign: '-2px' }} /> Replied</span>}
                {currentMessage.member && (
                  <span><User size={13} style={{ verticalAlign: '-2px' }} /> {currentMessage.member.name}</span>
                )}
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ background: 'var(--navy)', color: 'white', borderRadius: '12px 12px 12px 2px', padding: '12px 16px', maxWidth: '85%', fontSize: '13px', lineHeight: '1.6', alignSelf: 'flex-start' }}>
                  {currentMessage.message}
                  <div style={{ fontSize: '10px', opacity: 0.5, marginTop: '4px' }}>{formatDateTime(currentMessage.created_at)}</div>
                </div>
                
                {currentMessage.reply_message && (
                  <div style={{ background: 'var(--gold-pale)', border: '1px solid #f0dfa0', borderRadius: '12px 12px 2px 12px', padding: '12px 16px', maxWidth: '85%', fontSize: '13px', lineHeight: '1.6', alignSelf: 'flex-end' }}>
                    {currentMessage.reply_message}
                    <div style={{ fontSize: '10px', color: 'var(--text-light)', marginTop: '4px' }}>
                      Replied {formatDateTime(currentMessage.replied_at)}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ padding: '14px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: '10px', flexShrink: 0 }}>
                <textarea
                  className="form-textarea"
                  style={{ minHeight: '60px', flex: 1, resize: 'none' }}
                  placeholder={`Reply to ${currentMessage.sender_name || 'Unknown'}…`}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      handleSendReply();
                    }
                  }}
                  title="Type your reply (Ctrl+Enter to send)"
                  disabled={isSubmitting || loading}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <button 
                    className="btn btn-gold btn-sm" 
                    onClick={handleSendReply} 
                    title="Send reply (Ctrl+Enter)"
                    disabled={isSubmitting || !replyText.trim() || loading}
                  >
                    {isSubmitting ? (
                      <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Sending...</>
                    ) : (
                      'Send ↗'
                    )}
                  </button>
                  <button 
                    className="btn btn-ghost btn-sm" 
                    onClick={handleEscalate} 
                    title="Escalate to pastor"
                    disabled={isSubmitting || loading}
                  >
                    {isSubmitting ? (
                      <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Escalating...</>
                    ) : (
                      <><Siren size={14} style={{ verticalAlign: '-2px' }} /> Escalate</>
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <div className="empty-icon"><Mail size={40} strokeWidth={1.5} /></div>
              <div className="empty-title">Select a message</div>
              <div className="empty-text">Choose a message from the list to view details</div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 1024px) {
          .grid-35-65 {
            grid-template-columns: 1fr 1fr !important;
          }
        }
        @media (max-width: 768px) {
          .grid-35-65 {
            grid-template-columns: 1fr !important;
            grid-template-rows: 1fr 1fr;
          }
          .inbox-container {
            height: auto !important;
            min-height: calc(100vh - 140px);
          }
          .inbox-container .grid-35-65 {
            height: auto !important;
          }
          .inbox-container .card {
            height: 400px !important;
          }
          .inbox-item {
            padding: 10px 12px;
          }
          .inbox-item .inbox-preview {
            max-width: 120px;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
          }
        }
        @media (max-width: 480px) {
          .inbox-item {
            flex-wrap: wrap;
          }
          .inbox-item .inbox-preview {
            max-width: 100%;
          }
          .inbox-item .inbox-time {
            margin-left: 0;
            width: 100%;
            text-align: left;
            margin-top: 4px;
          }
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
          .inbox-container .card {
            height: 350px !important;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .inbox-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          border-bottom: 1px solid var(--border);
          transition: background 0.2s;
        }
        .inbox-item:hover {
          background: var(--cream);
        }
        .inbox-item.unread {
          background: var(--gold-pale);
        }
        .inbox-item.unread:hover {
          background: #f0e8d0;
        }
        .inbox-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--gold);
          flex-shrink: 0;
        }
        .inbox-name {
          font-size: 13px;
          font-weight: 600;
          color: var(--navy);
        }
        .inbox-preview {
          font-size: 12px;
          color: var(--text-mid);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 150px;
        }
        .inbox-time {
          font-size: 10px;
          color: var(--text-light);
          white-space: nowrap;
        }
        .badge-green {
          background: var(--green);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-red {
          background: var(--red);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-amber {
          background: var(--amber);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .form-textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid var(--border);
          border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          transition: border-color 0.2s;
          background: var(--white);
        }
        .form-textarea:focus {
          outline: none;
          border-color: var(--gold);
        }
      `}</style>
    </div>
  );
};

export default Inbox;